import { isKnownItemType } from '@sciolingo/api-client'
import { api } from '../api'
import { db } from '../db/db'
import type { CachedItem, LocalResponse, LocalSession } from '../db/types'
import { getClockOffsetMs, serverNow } from './clock'
import { evaluateLocally } from './evaluate'
import { flush, scheduleFlush } from './sync'

/** The session lifecycle of §5.1, from the Start tap onwards. */

export async function startSession(input: {
  descriptorId: string
  preparationId: string
  itemIds: string[]
  elapsedSecOverride?: number
}): Promise<LocalSession> {
  // Online and required — §5.1. Entitlement is checked here, server-side (§8.3),
  // which is why T1 needs no offline entitlement grace at all.
  const payload = await api.createSession({
    descriptorId: input.descriptorId,
    preparationId: input.preparationId,
    ...(input.elapsedSecOverride === undefined ? {} : { elapsedSecOverride: input.elapsedSecOverride }),
  })

  const session: LocalSession = {
    id: payload.id,
    kind: payload.kind,
    descriptorId: input.descriptorId,
    itemIds: payload.itemIds,
    startedAtServer: payload.startedAt,
    // §11: measured against the response that issued `startedAt`, so the two
    // agree even if the device clock is wrong or drifts later.
    clockOffsetMs: getClockOffsetMs(),
    durationSec: payload.durationSec,
    status: 'active',
    grantToken: payload.grantToken ?? '',
  }

  await db.sessions.put(session)
  return session
}

export interface RecordAnswerInput {
  session: LocalSession
  item: CachedItem
  answer: unknown
  timeSpentMs: number
}

/**
 * §6.1: written to IndexedDB **synchronously on answer, before any UI
 * transition**, never buffered only in memory. Responses are the only
 * irreplaceable data in the client and they are kilobytes — items and assets
 * are disposable because they are re-fetchable.
 */
export async function recordAnswer(input: RecordAnswerInput): Promise<LocalResponse> {
  const { session, item, answer, timeSpentMs } = input

  const existing = await db.responses
    .where('sessionId')
    .equals(session.id)
    .filter((r) => r.itemId === item.id)
    .first()

  const response: LocalResponse = {
    id: existing?.id ?? crypto.randomUUID(),
    sessionId: session.id,
    itemId: item.id,
    itemVersion: item.version,
    answer,
    answeredAt: serverNow(),
    timeSpentMs,
    // Provisional and display-only. The server's verdict replaces it on sync.
    localResult: evaluateLocally(item, answer),
    syncState: 'pending',
    attempts: 0,
  }

  await db.responses.put(response)
  scheduleFlush()
  return response
}

/** Everything the session runner needs to resume a session from IndexedDB. */
export interface SessionRun {
  session: LocalSession
  /** Only the items this client can render, in session order. */
  items: CachedItem[]
  /** Keyed by item id. */
  responses: Map<string, LocalResponse>
  /** The first unanswered item, or the last item when every item is answered. */
  resumeIndex: number
}

export async function loadSessionRun(sessionId: string): Promise<SessionRun | null> {
  const session = await db.sessions.get(sessionId)
  if (!session) return null

  const cached = await db.items.bulkGet(session.itemIds)
  // §7.4: items this client cannot render are skipped, with the skip visible
  // rather than silent. A malformed or future item type must not be able to
  // end a student's session.
  const items = cached.filter((i): i is CachedItem => i !== undefined && isKnownItemType(i.type))

  const stored = await db.responses.where('sessionId').equals(sessionId).toArray()
  const responses = new Map(stored.map((r) => [r.itemId, r]))

  // Resume where the student left off rather than at the top.
  const firstUnanswered = items.findIndex((i) => !responses.has(i.id))
  const resumeIndex = firstUnanswered === -1 ? Math.max(0, items.length - 1) : firstUnanswered

  return { session, items, responses, resumeIndex }
}

export interface SessionResult {
  session: LocalSession
  responses: LocalResponse[]
}

export async function loadSessionResult(sessionId: string): Promise<SessionResult | null> {
  const session = await db.sessions.get(sessionId)
  if (!session) return null
  return { session, responses: await db.responses.where('sessionId').equals(sessionId).toArray() }
}

const completing = new Map<string, Promise<LocalSession | undefined>>()

/**
 * §5.1, the last leg. Online this reaches `POST /complete` and returns the
 * authoritative result; offline it settles for a provisional raw score with the
 * percentile pending, and the sync happens on next connectivity or next app open.
 *
 * Several callers may race to settle the same session: app open, the result
 * screen and a StrictMode double effect. Concurrent calls share one attempt.
 */
export function completeSession(sessionId: string): Promise<LocalSession | undefined> {
  const inflight = completing.get(sessionId)
  if (inflight) return inflight

  const attempt = completeOnce(sessionId).finally(() => completing.delete(sessionId))
  completing.set(sessionId, attempt)
  return attempt
}

async function completeOnce(sessionId: string): Promise<LocalSession | undefined> {
  await db.sessions.update(sessionId, { status: 'completed', completedAtLocal: serverNow() })

  // Responses first: completing before the answers have landed would ask the
  // server to score a session it has not fully seen.
  await flush()

  const session = await db.sessions.get(sessionId)
  if (!session) return undefined

  const pending = await db.responses
    .where('sessionId')
    .equals(sessionId)
    .filter((r) => r.syncState !== 'synced')
    .count()

  if (pending > 0) return session

  try {
    const result = await api.completeSession({ sessionId, grantToken: session.grantToken })
    // §5.3: on sync the server result **replaces** the local one. Divergence
    // resolves silently in the server's favour and is logged for the content team.
    await db.sessions.update(sessionId, {
      status: 'settled',
      serverResult: {
        rawScore: result.rawScore,
        maxScore: result.maxScore,
        percentile: result.percentile,
        perItem: result.perItem,
        completedAt: result.completedAt,
        durationOverrunFlagged: result.durationOverrunFlagged,
      },
    })
  } catch (error) {
    console.warn('[session] complete could not reach the server; result stays provisional', error)
  }

  return db.sessions.get(sessionId)
}

/** Retried on app open and on reconnect for any session finished while offline. */
export async function settlePendingCompletions(): Promise<void> {
  const completed = await db.sessions.where('status').equals('completed').toArray()
  for (const session of completed) await completeSession(session.id)
}
